import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GroupedLaptop, formatGroupPriceRange } from '@/lib/laptopGrouping';
import { setGroupAskingPriceRpc } from '@/services/laptops';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Tag, Loader2, Check, AlertCircle } from 'lucide-react';

interface SetGroupPriceModalProps {
  group: GroupedLaptop | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const SetGroupPriceModal: React.FC<SetGroupPriceModalProps> = ({
  group,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [askingPrice, setAskingPrice] = useState<string>(
    group ? String(group.maxPrice || group.minPrice || '') : ''
  );
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<{ updated: number; skipped: number } | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!group) throw new Error('No group selected');
      const numericPrice = Number(askingPrice);
      if (!numericPrice || numericPrice <= 0) {
        throw new Error('Please enter a valid asking price greater than 0.');
      }

      return setGroupAskingPriceRpc({
        brand: group.brand,
        model: group.model,
        cpu: group.cpu,
        touchscreen: group.touchscreen ?? null,
        asking_price: numericPrice,
        reason: reason.trim() || 'Bulk group asking price update',
      });
    },
    onSuccess: (res) => {
      setResultSummary({ updated: res.updated_count, skipped: res.skipped_count });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    },
    onError: (err) => {
      setError(parseDatabaseError(err));
    },
  });

  if (!group) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} title={`Set Group Asking Price — ${group.brand} ${group.model}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {error && <ErrorAlert message={error} />}

        {resultSummary && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-900 flex items-center space-x-2 font-semibold">
            <Check className="w-4 h-4 text-green-700" />
            <span>
              Successfully updated {resultSummary.updated} in-stock laptop(s) to Rs. {Number(askingPrice).toLocaleString('en-PK')}.
              {resultSummary.skipped > 0 && ` (${resultSummary.skipped} non-stock units preserved).`}
            </span>
          </div>
        )}

        {/* Group Summary Box */}
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
          <div className="flex items-center justify-between font-bold text-brand-black text-sm">
            <span>{group.brand} {group.model}</span>
            {group.touchscreen === true && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded font-bold">
                Touchscreen
              </span>
            )}
            {group.touchscreen === false && (
              <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-[10px] rounded font-bold">
                Non-Touch
              </span>
            )}
          </div>
          <div className="flex items-center justify-between text-brand-muted text-[11px]">
            <span>CPU: {group.cpu || 'Base CPU'}</span>
            <span>Current Asking: {formatGroupPriceRange(group.minPrice, group.maxPrice)}</span>
          </div>
          <div className="flex items-center space-x-1.5 text-amber-900 bg-amber-50 p-2 rounded border border-amber-200 text-[11px] mt-2">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              This will update <strong>asking_price</strong> strictly for all <strong>{group.inStockQuantity}</strong> in-stock, available machines in this group. Historical sold, scrapped, and archived records remain unaffected.
            </span>
          </div>
        </div>

        {/* Input Fields */}
        <div>
          <label className="block text-xs font-bold text-brand-black uppercase tracking-wider mb-1">
            New Group Asking Price (PKR) *
          </label>
          <input
            type="number"
            min={1}
            placeholder="e.g. 85000"
            value={askingPrice}
            onChange={(e) => setAskingPrice(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-brand-border rounded text-sm font-bold text-brand-green focus:ring-2 focus:ring-brand-green"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-brand-black uppercase tracking-wider mb-1">
            Reason / Notes (Optional)
          </label>
          <input
            type="text"
            placeholder="e.g. Market price adjustment / seasonal discount"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-1.5 bg-white border border-brand-border rounded text-xs"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-2 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-3 py-2 text-xs font-semibold text-brand-muted hover:text-brand-black"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !askingPrice}
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover transition-colors shadow-sm disabled:opacity-50"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
            <span>{mutation.isPending ? 'Updating...' : 'Update In-Stock Asking Prices'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
